import styled from "@emotion/styled"
import { PHeading, PText } from "@porsche-design-system/components-react"
import type { PHeadingProps, PTextProps } from "@porsche-design-system/components-react"
import { spacingStaticSm, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { FinancingFormProductDetailsView } from "@slfinrtl/ui-bff-routes"
import type { PropsWithChildren } from "react"
import { defineMessages, useIntl } from "react-intl"
import { List } from "../list"
import { FinancingFormProductDetailsSkeleton } from "./FinancingFormProductDetailsSkeleton"
import type { FinancingNotification } from "./FinancingNotification"
import { Information } from "./Information"
import { mapIcon } from "./mapIcon"

interface StateLoading {
	state: "loading"
}

interface StateReady {
	product: FinancingFormProductDetailsView | null
	productType: string
	state: "ready"
}

interface StateNotification extends FinancingNotification {
	state: "notification"
}

type FinancingFormProductDetailsProps = StateLoading | StateReady | StateNotification

const messages = defineMessages({
	noSelectedProductText: {
		// eslint-disable-next-line formatjs/blocklist-elements
		defaultMessage: `This vehicle is displayed without {
			productType, select,
              LEASING {Leasing}
              THREEWAY_LOAN {Financing}
              RENTING {Renting}
              other {{productType}}
            } in the Finder`,
		id: "vehicleDetails.overview.financing.form.details.noSelectedProduct.text",
	},
	noSelectedProductTitle: {
		// eslint-disable-next-line formatjs/blocklist-elements
		defaultMessage: `No {
			productType, select,
              LEASING {Leasing}
              THREEWAY_LOAN {Financing}
              RENTING {Renting}
              other {{productType}}
            } selected.`,
		id: "vehicleDetails.overview.financing.form.details.noSelectedProduct.title",
	},
})

export const FinancingFormProductDetails = (props: FinancingFormProductDetailsProps) => {
	const { state } = props
	const { formatMessage } = useIntl()

	if (state === "loading") {
		return <FinancingFormProductDetailsSkeleton />
	}

	if (state === "notification") {
		const { severity, text, title } = props
		return <Information icon={mapIcon(severity)} title={title} text={text} />
	}

	if (state !== "ready") {
		return null
	}

	const { product, productType } = props

	if (product === null) {
		return (
			<Information
				icon="information"
				title={formatMessage(messages.noSelectedProductTitle, {
					productType,
				})}
				text={formatMessage(messages.noSelectedProductText, {
					productType,
				})}
			/>
		)
	}

	const { description, header, name, summary } = product

	return (
		<>
			<List
				header={
					<>
						{header?.title && <StyledHeading>{header.title}</StyledHeading>}
						{header?.text && <Description>{header.text}</Description>}
						<StyledHeading>{name}</StyledHeading>
						{description && <Description>{description}</Description>}
					</>
				}
			>
				{summary.items.map((item) => (
					<List.Item key={item.label} {...item} />
				))}
			</List>
			{summary.hint && <ProductTaxInfo>{summary.hint}</ProductTaxInfo>}
		</>
	)
}

const StyledHeading = styled(({ children, ...rest }: PropsWithChildren<PHeadingProps>) => (
	<PHeading size="lg" tag="h2" {...rest}>
		{children}
	</PHeading>
))`
	margin: 0 0 ${spacingStaticMd};
`

const Description = styled(({ children, ...rest }: PropsWithChildren<PTextProps>) => (
	<PText color="contrast-medium" size="xs" {...rest}>
		{children}
	</PText>
))`
	margin: 0 0 ${spacingStaticMd};
`

const ProductTaxInfo = styled(({ children, ...rest }: PropsWithChildren<PTextProps>) => (
	<PText align="end" color="contrast-medium" size="xs" {...rest}>
		{children}
	</PText>
))`
	margin: ${spacingStaticSm} 0 0
`
